import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getPlan, removePlan, updatePlan } from 'slice/planSlice';

const createEmptyPlan = () => ({
  id: '',
  name: '',
  price: '',
  duration_days: '',
  description: '',
});

const getLinkedMedicines = (plan) => {
  if (!Array.isArray(plan?.plan_medicines)) {
    return [];
  }

  return plan.plan_medicines.map((item) => item?.medicine?.name).filter(Boolean);
};

const PlanList = () => {
  const dispatch = useDispatch();
  const planState = useSelector((state) => state.plan);
  const [showModal, setShowModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(createEmptyPlan());

  useEffect(() => {
    dispatch(getPlan());
  }, [dispatch]);

  const handleEditClick = (plan) => {
    setCurrentPlan({
      id: plan.id,
      name: plan.name || '',
      price: plan.price ?? '',
      duration_days: plan.duration_days ?? '',
      description: plan.description || '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentPlan(createEmptyPlan());
    setShowModal(false);
  };

  const handleSave = () => {
    if (!currentPlan.name.trim()) {
      toast('Plan name is required');
      return;
    }

    dispatch(
      updatePlan({
        id: currentPlan.id,
        data: {
          name: currentPlan.name,
          price: currentPlan.price === '' ? null : Number(currentPlan.price),
          duration_days: currentPlan.duration_days === '' ? null : Number(currentPlan.duration_days),
          description: currentPlan.description || null,
        },
      })
    );

    toast('Plan updated successfully!');
    handleCloseModal();
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Price', accessor: 'price' },
    { Header: 'Duration Days', accessor: 'duration_days' },
    {
      Header: 'Linked Medicines',
      accessor: 'plan_medicines',
      Cell: ({ row }) => {
        const linkedMedicines = getLinkedMedicines(row.original);
        return linkedMedicines.length ? linkedMedicines.join(', ') : 'No medicines linked';
      },
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => <TruncatedMessage text={row.original.description} maxLength={40} />,
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn removebtn mx-1 mb-2" onClick={() => dispatch(removePlan(row.original.id))}>
            Remove
          </button>
          <button className="btn editbtn mx-1" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Plans List</h4>
        </div>
        {planState?.data?.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={planState.data} />
        ) : (
          <div className="card shadow-sm border-0 mx-4 mt-4">
            <div className="card-body text-center py-5">
              <h5 className="mb-2">No plans found</h5>
              <p className="text-muted mb-0">Create a plan first to manage pricing and medicine mapping.</p>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Plan</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="editPlanName">Plan Name</label>
                  <input
                    id="editPlanName"
                    type="text"
                    className="form-control"
                    value={currentPlan.name}
                    onChange={(event) => setCurrentPlan((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </div>
                <div className="row">
                  <div className="col-sm-6 form-group">
                    <label htmlFor="editPlanPrice">Price</label>
                    <input
                      id="editPlanPrice"
                      type="number"
                      min="0"
                      className="form-control"
                      value={currentPlan.price}
                      onChange={(event) => setCurrentPlan((prev) => ({ ...prev, price: event.target.value }))}
                    />
                  </div>
                  <div className="col-sm-6 form-group">
                    <label htmlFor="editPlanDuration">Duration Days</label>
                    <input
                      id="editPlanDuration"
                      type="number"
                      min="0"
                      className="form-control"
                      value={currentPlan.duration_days}
                      onChange={(event) => setCurrentPlan((prev) => ({ ...prev, duration_days: event.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="editPlanDescription">Description</label>
                  <textarea
                    id="editPlanDescription"
                    rows={4}
                    className="form-control"
                    value={currentPlan.description}
                    onChange={(event) => setCurrentPlan((prev) => ({ ...prev, description: event.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanList;
